import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TeamRankingSection from './TeamRankingSection.jsx';

const TEAM_RANKING_TABS = {
  rugby: [
    { key: 'best_attack', title: 'POINTS MARQUÉS', shortLabel: 'Pts +' },
    { key: 'best_attack_home', title: 'POINTS MARQUÉS DOMICILE', shortLabel: 'Pts + dom.' },
    { key: 'best_attack_away', title: 'POINTS MARQUÉS EXTÉRIEUR', shortLabel: 'Pts + ext.' },
    { key: 'best_defense', title: 'POINTS ENCAISSÉS', shortLabel: 'Pts -' },
    { key: 'best_defense_home', title: 'POINTS ENCAISSÉS DOMICILE', shortLabel: 'Pts - dom.' },
    { key: 'best_defense_away', title: 'POINTS ENCAISSÉS EXTÉRIEUR', shortLabel: 'Pts - ext.' },
    { key: 'best_try_scorers', title: 'ESSAIS MARQUÉS', shortLabel: 'Essais +' },
    { key: 'best_try_defenders', title: 'ESSAIS ENCAISSÉS', shortLabel: 'Essais -' },
  ],
  soccer: [
    { key: 'best_attack', title: 'BUTS MARQUÉS', shortLabel: 'Buts +' },
    { key: 'best_attack_home', title: 'BUTS MARQUÉS DOMICILE', shortLabel: 'Buts + dom.' },
    { key: 'best_attack_away', title: 'BUTS MARQUÉS EXTÉRIEUR', shortLabel: 'Buts + ext.' },
    { key: 'best_defense', title: 'BUTS ENCAISSÉS', shortLabel: 'Buts -' },
    { key: 'best_defense_home', title: 'BUTS ENCAISSÉS DOMICILE', shortLabel: 'Buts - dom.' },
    { key: 'best_defense_away', title: 'BUTS ENCAISSÉS EXTÉRIEUR', shortLabel: 'Buts - ext.' },
  ],
};

const getTabIndexFromStatType = (tabs, statType) => {
  const index = tabs.findIndex((tab) => tab.key === statType);
  return index === -1 ? 0 : index;
};

const getDefaultSortByStatType = (statType) => {
  switch (statType) {
    case 'best_defense':
    case 'best_defense_home':
    case 'best_defense_away':
    case 'best_try_defenders':
      return 'value_asc';
    case 'best_attack':
    case 'best_attack_home':
    case 'best_attack_away':
    case 'best_try_scorers':
    default:
      return 'value_desc';
  }
};

export default function TeamRankingWidget({
  discipline = 'rugby',
  competitionIds = [],
  season,
  onNavigate,
  widgetConfigurationId = null,
  title,
  showCompetitionInfos = true,
  defaultLimit = 20,
  limitOptions = [10, 20, 50, 100],
  statType = '',
  onStatTypeChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabs = useMemo(() => TEAM_RANKING_TABS[discipline] || [], [discipline]);
  const activeTab = getTabIndexFromStatType(tabs, statType);

  const currentTab = tabs[activeTab];
  const currentSort = getDefaultSortByStatType(currentTab?.key);

  const handleTabChange = (_, value) => {
    const nextStatType = tabs[value]?.key;
    if (!nextStatType) return;

    onStatTypeChange?.(nextStatType, {
      isDefaultTab: value === 0,
    });
  };

  if (!tabs.length) return null;

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={2.5}>
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#1f1f1f',
              }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: { display: 'none' },
              }}
              sx={{
                minHeight: 0,
                '& .MuiTabs-flexContainer': {
                  gap: 1,
                },
              }}
            >
              {tabs.map((tab, index) => {
                const selected = index === activeTab;

                return (
                  <Tab
                    key={tab.key}
                    disableRipple
                    label={isMobile ? tab.shortLabel : tab.title}
                    sx={{
                      minHeight: 28,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: '999px',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      fontSize: { xs: 10, md: 11 },
                      minWidth: 'auto',
                      color: selected ? '#111827' : '#6b7280',
                      bgcolor: selected ? '#e5f2e6' : 'transparent',
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          {showCompetitionInfos && season ? (
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Saison {season}
            </Typography>
          ) : null} 

          {currentTab ? (
            <TeamRankingSection
              statType={currentTab.key}
              discipline={discipline}
              competitionIds={competitionIds}
              season={season}
              widgetConfigurationId={widgetConfigurationId}
              limit={defaultLimit}
              limitOptions={limitOptions}
              sort={currentSort}
              onNavigate={onNavigate}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}